// =============================================================================
// BP-08-05: Vendor Portal Frontend - useVendorAnalytics Hook
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { vendorAnalyticsApi } from '@/features/vendor/api/vendorApi';
import type { AnalyticsTimeframe, VendorAnalytics } from '@/features/vendor/types/vendorTypes';
import { useMemo } from 'react';

// Query keys
export const vendorAnalyticsKeys = {
  all: ['vendor-analytics'] as const,
  analytics: (timeframe: AnalyticsTimeframe) => [...vendorAnalyticsKeys.all, timeframe] as const,
  views: (timeframe: AnalyticsTimeframe) => [...vendorAnalyticsKeys.all, 'views', timeframe] as const,
  inquiries: (timeframe: AnalyticsTimeframe) => [...vendorAnalyticsKeys.all, 'inquiries', timeframe] as const,
  revenue: (timeframe: AnalyticsTimeframe) => [...vendorAnalyticsKeys.all, 'revenue', timeframe] as const,
  topProducts: (limit: number) => [...vendorAnalyticsKeys.all, 'top-products', limit] as const,
};

// Preset timeframes
export type TimeframePreset = '7d' | '30d' | '90d' | '12m';

export function getTimeframeFromPreset(preset: TimeframePreset): AnalyticsTimeframe {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];
  
  let startDate: string;
  let granularity: AnalyticsTimeframe['granularity'];
  
  switch (preset) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      granularity = 'day';
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      granularity = 'day';
      break;
    case '90d':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      granularity = 'week';
      break;
    case '12m':
      startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().split('T')[0];
      granularity = 'month';
      break;
    default:
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      granularity = 'day';
  }
  
  return { startDate, endDate, granularity };
}

/**
 * Hook for fetching full vendor analytics
 */
export function useVendorAnalytics(timeframe: AnalyticsTimeframe) {
  return useQuery({
    queryKey: vendorAnalyticsKeys.analytics(timeframe),
    queryFn: () => vendorAnalyticsApi.get(timeframe),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for fetching analytics with preset timeframe
 */
export function useVendorAnalyticsPreset(preset: TimeframePreset) {
  const timeframe = useMemo(() => getTimeframeFromPreset(preset), [preset]);
  return useVendorAnalytics(timeframe);
}

/**
 * Hook for fetching views analytics
 */
export function useVendorViews(timeframe: AnalyticsTimeframe) {
  return useQuery({
    queryKey: vendorAnalyticsKeys.views(timeframe),
    queryFn: () => vendorAnalyticsApi.getViews(timeframe),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching inquiries analytics
 */
export function useVendorInquiries(timeframe: AnalyticsTimeframe) {
  return useQuery({
    queryKey: vendorAnalyticsKeys.inquiries(timeframe),
    queryFn: () => vendorAnalyticsApi.getInquiries(timeframe),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching revenue analytics
 */
export function useVendorRevenue(timeframe: AnalyticsTimeframe) {
  return useQuery({
    queryKey: vendorAnalyticsKeys.revenue(timeframe),
    queryFn: () => vendorAnalyticsApi.getRevenue(timeframe),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching top products
 */
export function useTopProducts(limit = 10) {
  return useQuery({
    queryKey: vendorAnalyticsKeys.topProducts(limit),
    queryFn: () => vendorAnalyticsApi.getTopProducts(limit),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Format change percentage for display
 */
export function formatChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

/**
 * Get change indicator style
 */
export function getChangeStyle(change: number): { color: string; icon: 'up' | 'down' | 'neutral' } {
  if (change > 0) {
    return { color: 'text-green-600', icon: 'up' };
  } else if (change < 0) {
    return { color: 'text-red-600', icon: 'down' };
  }
  return { color: 'text-gray-500', icon: 'neutral' };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format large numbers (1000 -> 1K)
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
}

